import { useEffect, useState } from "react";
import { PageHead, Reveal, Tag } from "@/components/ui-kit";

const SECTIONS = [
  { id: "machine-problem", num: "01", title: "THE MACHINE PROBLEM", body: [
    "Machines are becoming increasingly capable of perception, reasoning, planning and physical execution. A modern robot can see, decide and act. But intelligence alone is not enough.",
    "An autonomous machine that cannot identify itself, prove its work, or pay for the resources it consumes remains dependent on a human operator for every economic interaction. That dependency is the bottleneck of the machine economy.",
  ] },
  { id: "autonomous-economies", num: "02", title: "FROM AUTONOMOUS ROBOTS TO AUTONOMOUS ECONOMIES", body: [
    "Autonomy is usually framed as a robotics problem: better sensors, better planning, better actuation. ARCMECH frames it as an infrastructure problem. A robot that can act but cannot transact is only half autonomous.",
    "Autonomous machines need identity, communication, coordination, reputation, access to resources, economic capabilities and programmable payments. When those primitives exist, individual robots stop being tools and start becoming participants.",
  ] },
  { id: "machine-identity", num: "03", title: "MACHINE IDENTITY", body: [
    "Every economic system begins with identity. A machine identity is a persistent, verifiable handle that binds a physical unit to its capabilities, its history and its wallet.",
    "With persistent identity, a rover is no longer an anonymous device. It is MECH-031 — a counterpart that other agents can discover, evaluate, hire and pay.",
  ] },
  { id: "machine-wallets", num: "04", title: "MACHINE WALLETS", body: [
    "If machines are to participate in an economy, they must be able to hold and move value. A machine wallet gives an autonomous system the ability to receive payment for work and to purchase the resources it needs — compute, energy, data, transport.",
    "Machine wallets turn physical capability into economic agency. The machine stops being a cost center and becomes a balance sheet.",
  ] },
  { id: "m2m-payments", num: "05", title: "MACHINE-TO-MACHINE PAYMENTS", body: [
    "Most commerce assumes a human on at least one side of every transaction. Machine-to-machine payments remove that assumption: a drone purchases a mapping service from a digital agent; a rover pays a charging station; an industrial arm sells spare capacity to another factory.",
    "Programmable payments make these interactions atomic, verifiable and fast enough to match machine timescales rather than human billing cycles.",
  ] },
  { id: "task-economy", num: "06", title: "THE AUTONOMOUS TASK ECONOMY", body: [
    "Work enters the system as tasks: inspect this structure, map this warehouse, scan this solar farm. AI agents decompose objectives, discover machines with the required capabilities, and assign execution.",
    "The task market is the heartbeat of the machine economy — a continuous flow of discovery, bidding, execution and verification that no human scheduler could operate at scale.",
  ] },
  { id: "machine-reputation", num: "07", title: "MACHINE REPUTATION", body: [
    "Trust between strangers is a solved problem in human markets: ratings, history, guarantees. Machines need the same primitive. Every executed task, every verified delivery, every settled payment feeds a machine's reputation.",
    "Reputation makes the network self-organizing. Reliable machines attract better tasks. Unreliable machines are routed around — automatically.",
  ] },
  { id: "agent-coordination", num: "08", title: "AGENT-TO-AGENT COORDINATION", body: [
    "No single agent sees the whole system. Coordinators, planners, verification oracles and brokers must negotiate with each other: one agent holds the objective, another holds the machines, a third holds the proof standard.",
    "Agent-to-agent coordination is where the machine economy develops something like institutions — protocols for agreeing on price, quality and settlement without human arbitration.",
  ] },
  { id: "why-arc", num: "09", title: "WHY ARC", body: [
    "Machine commerce needs a settlement environment designed for high-frequency, low-friction, programmable value transfer. Settlement must be fast enough for machine timescales and cheap enough for micropayments.",
    "ARC provides the settlement environment in the ARCMECH architecture — the layer where task verification converts into final payment.",
  ] },
  { id: "usdc-machine-money", num: "10", title: "USDC AS MACHINE MONEY", body: [
    "Machines do not speculate; they budget. A machine that prices a task needs a unit of account that stays stable between the moment work is quoted and the moment payment settles.",
    "USDC can act as the machine economy's unit of account — a stable, programmable medium that lets a rover quote 12.50 for an inspection and receive exactly that.",
  ] },
  { id: "flywheel", num: "11", title: "THE MACHINE ECONOMY FLYWHEEL", body: [
    "More machines attract more tasks. More tasks generate more reputation and more payment history. Better reputation attracts better machines. Better machines justify more ambitious tasks.",
    "Each rotation compounds. Identity feeds discovery, discovery feeds execution, execution feeds reputation, reputation feeds demand — and settlement ties the loop closed.",
  ], quote: "An economy is not a market. It is a flywheel of trust, work and settlement." },
  { id: "long-term-vision", num: "12", title: "THE LONG-TERM VISION", body: [
    "The long-term vision is an economy where a meaningful share of physical work is discovered, coordinated, executed and paid for autonomously — with humans defining objectives and setting the rules, and machines handling the rest.",
    "This does not arrive as a single product launch. It arrives as infrastructure: identity, wallets, markets, reputation and settlement, each making the others more useful.",
  ] },
  { id: "final-thesis", num: "13", title: "FINAL THESIS", body: [
    "ARCMECH explores an infrastructure where AI agents and physical machines can discover tasks, coordinate with other agents, execute work, and transact autonomously.",
    "The human defines the objective. The AI agent coordinates execution. The machine performs the physical work. ARC provides the settlement environment. Machines That Think. Machines That Transact.",
  ], quote: "Intelligence gave machines the ability to act. Economic infrastructure gives them the ability to participate." },
];

const ThesisPage = () => {
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setProgress(max > 0 ? (h.scrollTop / max) * 100 : 0);
      let current = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top < 160) current = s.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="thesis-page" className="pb-24">
      <div className="fixed top-16 left-0 right-0 h-0.5 z-40 bg-arc-metal/50" aria-hidden="true">
        <div data-testid="scroll-progress" className="h-full bg-gradient-to-r from-arc-blue to-arc-cyan" style={{ width: `${progress}%` }} />
      </div>
      <PageHead
        eyebrow="MODULE 07 // RESEARCH"
        title="THE ARCMECH THESIS"
        subtitle="A long-form examination of what happens when intelligence, robotics and programmable economic infrastructure converge."
      />
      <div className="px-5 sm:px-8 max-w-7xl mx-auto grid lg:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden lg:block">
          <nav className="sticky top-28 space-y-1" aria-label="Thesis table of contents">
            <div className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/40 mb-3">CONTENTS</div>
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`toc-${s.id}`}
                data-cursor="OPEN"
                className={`block font-mono text-[11px] tracking-wider py-1.5 pl-3 border-l transition-colors duration-200 ${
                  activeId === s.id ? "border-arc-electric text-arc-cyan" : "border-arc-silver/15 text-arc-silver/45 hover:text-arc-silver"
                }`}
              >
                {s.num} · {s.title}
              </a>
            ))}
          </nav>
        </aside>
        <article className="max-w-3xl">
          {SECTIONS.map((s) => (
            <Reveal key={s.id}>
              <section id={s.id} className="scroll-mt-28 mb-16 relative">
                <div className="font-display text-5xl font-extrabold text-arc-blue/10 select-none absolute -top-6 -left-1" aria-hidden="true">{s.num}</div>
                <h2 className="font-display text-xl sm:text-2xl font-bold tracking-wide text-arc-white uppercase relative">{s.title}</h2>
                <div className="w-10 h-px bg-arc-electric mt-3 mb-5" aria-hidden="true" />
                {s.body.map((p, i) => (
                  <p key={i} className="text-sm sm:text-base text-arc-silver/75 leading-relaxed mb-4">{p}</p>
                ))}
                {s.quote && (
                  <blockquote className="my-6 border-l-2 border-arc-electric bg-arc-navy/50 px-5 py-4">
                    <p className="font-display text-base sm:text-lg text-arc-cyan leading-relaxed">"{s.quote}"</p>
                  </blockquote>
                )}
              </section>
            </Reveal>
          ))}
          <Reveal>
            <div className="hud-panel p-5 font-mono text-xs leading-relaxed text-arc-silver/70">
              <div className="text-arc-electric tracking-[0.3em] text-[10px] mb-3">SUMMARY // TERMINAL</div>
              <div>&gt; objective.defined_by = HUMAN</div>
              <div>&gt; execution.coordinated_by = AI_AGENT</div>
              <div>&gt; work.performed_by = MACHINE</div>
              <div>&gt; settlement.layer = <span className="text-arc-cyan">ARC</span></div>
              <div>&gt; unit_of_account = <span className="text-arc-cyan">USDC</span></div>
            </div>
            <div className="mt-8"><Tag tone="silver">CONCEPTUAL RESEARCH — NO TECHNICAL SPECIFICATIONS ARE FINAL</Tag></div>
          </Reveal>
        </article>
      </div>
    </div>
  );
};

export default ThesisPage;
